/**
 * EnvironmentRepository - Repositório de Ambientes
 * Persiste ambientes de levantamentos no IndexedDB
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'environments';

/**
 * Gera ID local único
 */
const generateLocalId = () => {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `LOC-ENV-${dateStr}-${random}`;
};

/**
 * Cria novo ambiente
 */
export const create = async (surveyId, localSurveyId, environmentData) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const localId = generateLocalId();
    const now = new Date().toISOString();

    const environment = {
      ...environmentData,
      id: localId,
      localId,
      surveyId,
      localSurveyId,
      syncStatus: 'pending',
      createdAt: now,
      updatedAt: now,
      serverId: null,
    };

    const request = store.add(environment);

    request.onsuccess = () => {
      resolve(environment);
    };

    request.onerror = () => {
      reject(new Error(`Erro ao criar ambiente: ${request.error}`));
    };
  });
};

/**
 * Atualiza ambiente existente
 */
export const update = async (id, data) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;

      if (!existing) {
        reject(new Error(`Ambiente com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        ...data,
        id: existing.id,
        localId: existing.localId,
        syncStatus: 'pending',
        updatedAt: new Date().toISOString(),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar ambiente: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar ambiente: ${getRequest.error}`));
  });
};

/**
 * Remove ambiente
 */
export const remove = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao remover ambiente: ${request.error}`));
  });
};

/**
 * Busca ambiente por ID
 */
export const getById = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar ambiente: ${request.error}`));
  });
};

/**
 * Busca ambiente por localId
 */
export const getByLocalId = async (localId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const request = index.get(localId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar ambiente: ${request.error}`));
  });
};

/**
 * Lista ambientes de um levantamento (por localSurveyId)
 */
export const getBySurveyLocalId = async (localSurveyId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localSurveyId');
    const request = index.getAll(localSurveyId);

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => (a.order || 0) - (b.order || 0));
      resolve(results);
    };
    request.onerror = () => reject(new Error(`Erro ao listar ambientes: ${request.error}`));
  });
};

/**
 * Lista ambientes pendentes de sync
 */
export const getPending = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('syncStatus');
    const request = index.getAll('pending');

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Erro ao buscar pendientes: ${request.error}`));
  });
};

/**
 * Atualiza status de sync
 */
export const updateSyncStatus = async (id, status, serverId = null) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Ambiente com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        syncStatus: status,
        serverId: serverId !== null ? serverId : existing.serverId,
        syncedAt: status === 'synced' ? new Date().toISOString() : existing.syncedAt,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar sync status: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar ambiente: ${getRequest.error}`));
  });
};

/**
 * Atualiza serverId após sincronização bem-sucedida
 */
export const updateServerId = async (localId, serverId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const getRequest = index.get(localId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Ambiente com localId ${localId} não encontrado`));
        return;
      }

      const updated = { ...existing, serverId, syncStatus: 'synced' };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar serverId: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar ambiente: ${getRequest.error}`));
  });
};

/**
 * Remove todos os ambientes de um levantamento
 */
export const removeBySurveyLocalId = async (localSurveyId) => {
  const db = await openDatabase();
  const environments = await getBySurveyLocalId(localSurveyId);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    let completed = 0;

    if (environments.length === 0) {
      resolve();
      return;
    }

    environments.forEach(env => {
      const request = transaction.objectStore(STORE_NAME).delete(env.id);
      request.onsuccess = () => {
        completed++;
        if (completed === environments.length) resolve();
      };
      request.onerror = () => reject(new Error(`Erro ao remover ambiente ${env.id}`));
    });
  });
};

export default {
  create,
  update,
  remove,
  getById,
  getByLocalId,
  getBySurveyLocalId,
  getPending,
  updateSyncStatus,
  updateServerId,
  removeBySurveyLocalId,
};
